// hooks/useNotifications.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import io from 'socket.io-client';
import { API_URL } from '../config/env';
import API from '../services/api';

// On garde en mémoire globalement si une connexion a déjà été faite pour cet utilisateur
const connectedUsers = new Set();
let instanceCounter = 0;

export const useNotifications = () => {
  const { user, token } = useAuth();

  // ✅ NOTIFICATIONS EXISTANTES (SSE)
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef(null);
  const [toast, setToast] = useState(null);
  const reconnectTimeoutRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const hasInitializedRef = useRef(false);
  const instanceIdRef = useRef(++instanceCounter);

  // ✅ NOUVELLES NOTIFICATIONS MESSAGES (Socket.IO)
  const [unreadMessages, setUnreadMessages] = useState(new Map()); // enfantId -> count
  const [totalUnreadMessages, setTotalUnreadMessages] = useState(0);
  const socketRef = useRef(null);

  // Vérifier si on est sur la page messages
  const isOnMessagesPage = false;

  // ✅ GESTION DES MESSAGES SOCKET.IO
  const initializeMessageSocket = useCallback(() => {
    if (!user || !token || socketRef.current) return;

    const apiUrl = API_URL;
    const socketUrl = new URL(apiUrl).origin;


    socketRef.current = io(socketUrl, {
      transports: ['websocket', 'polling'],
      auth: { token }
    });

    socketRef.current.on('connect', () => {

      socketRef.current.emit('auto-join-rooms');

      if (user._id) {
        socketRef.current.emit('joinParentRoom', { parentId: user._id });
      }
    });

    socketRef.current.on('auto-rooms-joined', (enfantIds) => {

    });

    // Écouter les nouveaux messages pour les notifications
    socketRef.current.on('nouveau_message', (data) => {

      
      // Ne notifier que si ce n'est pas notre propre message
      const isMyMessage = data.message.expediteur?._id === user._id || 
                         data.message.expediteur === user._id;
      
      if (!isMyMessage) {
        // Si on n'est pas sur la page messages, ou si on y est mais pas dans cette room
        if (!isOnMessagesPage || !isCurrentlyViewingRoom(data.enfantId)) {
          incrementUnreadMessageCount(data.enfantId);
          
          // Notification du navigateur
          showBrowserNotification(data);
        }
      }
    });

    socketRef.current.on('disconnect', () => {

    });

    socketRef.current.on('connect_error', (err) => {

    });
  }, [user, token, isOnMessagesPage]);

  // Vérifier si on regarde actuellement cette room
  const isCurrentlyViewingRoom = useCallback((enfantId) => {
    return localStorage.getItem('currentViewingRoom') === enfantId;
  }, []);

  // Incrémenter le compteur de messages non lus
  const incrementUnreadMessageCount = useCallback((enfantId) => {
    setUnreadMessages(prev => {
      const newMap = new Map(prev);
      const currentCount = newMap.get(enfantId) || 0;
      newMap.set(enfantId, currentCount + 1);

      return newMap;
    });
  }, []);

  // Marquer les messages comme lus
  const markMessagesAsRead = useCallback((enfantId) => {
    setUnreadMessages(prev => {
      const newMap = new Map(prev);
      newMap.delete(enfantId);

      return newMap;
    });
  }, []);

  // Marquer tous les messages comme lus
  const markAllMessagesAsRead = useCallback(() => {
    setUnreadMessages(new Map());

  }, []);

  // Obtenir le nombre de messages non lus pour un enfant
  const getUnreadMessageCount = useCallback((enfantId) => {
    return unreadMessages.get(enfantId) || 0;
  }, [unreadMessages]);

  // Notification du navigateur pour messages
  const showBrowserNotification = useCallback((data) => {
    if (!('Notification' in window)) return;

    if (Notification.permission === 'granted') {
      const notification = new Notification('Nouveau message', {
        body: `${data.message.expediteur?.prenom || 'Quelqu\'un'} vous a envoyé un message`,
        icon: '/favicon.ico',
        tag: `message-${data.enfantId}`
      });

      setTimeout(() => notification.close(), 5000);

      notification.onclick = () => {
        window.focus();
        // Optionnel: naviguer vers la room
      };
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          showBrowserNotification(data);
        }
      });
    }
  }, []);

  // Mettre à jour le total des messages non lus
  useEffect(() => {
    const total = Array.from(unreadMessages.values()).reduce((sum, count) => sum + count, 0);
    setTotalUnreadMessages(total);
  }, [unreadMessages]);

  // ✅ GESTION SSE EXISTANTE (notifications générales)
  const connectSSE = useCallback(() => {
    if (!user || !token || eventSourceRef.current) return;


    // NOTE: EventSource API does not support custom headers (Authorization).
    // Token must be passed as a query parameter. This is a known limitation.
    // Mitigations: use short-lived tokens, ensure server logs redact query strings.
    const eventSource = new EventSource(`/api/notifications/stream?token=${encodeURIComponent(token)}`);
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {

      setIsConnected(true);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);


      switch (data.type) {
        case 'notification':
          const newNotification = {
            id: data.id, _id: data.id, titre: data.titre, message: data.message,
            type: data.notificationType, notificationType: data.notificationType,
            timestamp: data.timestamp, creeA: data.timestamp,
            lue: false, read: false, enfant: data.enfant, expediteur: data.expediteur,
            metadata: data.metadata
          };
          setNotifications(prev => [newNotification, ...prev.slice(0, 9)]);
          setUnreadCount(prev => prev + 1);
          if (['nouveau_like', 'nouveau_commentaire', 'nouvelle_activite', 'phenix_reveal', 'phenix_spontane', 'invitation_acceptee', 'capsule_deverrouillee', 'capsule_bientot_ouverte'].includes(data.notificationType)) {
            showToast({ type: 'notification', title: data.titre, message: data.message, action: data.metadata?.action });
          }
          break;
        case 'heartbeat':

          break;
        default:

      }
    };

    eventSource.onerror = (error) => {

      setIsConnected(false);
      eventSource.close();
      if (!reconnectTimeoutRef.current) {
        reconnectTimeoutRef.current = setTimeout(() => {

          eventSourceRef.current = null;
          connectSSE();
        }, 5000);
      }
    };
  }, [user, token]);

  const loadInitialNotifications = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);


    try {
      const result = await API.notifications.getNotifications();

      if (result.success && Array.isArray(result.data)) {
        const initialNotifs = result.data.slice(0, 10);

        setNotifications(initialNotifs);
        setUnreadCount(initialNotifs.filter(n => !n.lue).length);
      }
    } catch (error) {

      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const disconnectSSE = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      setIsConnected(false);

    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const disconnectMessageSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;

    }
  }, []);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      const response = await API.notifications.marquerCommeLue(notificationId);

      if (response.success !== false) {
        setNotifications(prev =>
          prev.map(notif =>
            (notif.id === notificationId || notif._id === notificationId)
              ? { ...notif, lue: true, read: true }
              : notif
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));

      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.warn('Notification error:', error);
    }
  }, [token]);

  const markAllAsRead = useCallback(async () => {
    try {
      const response = await API.notifications.marquerToutesLues();

      if (response.success !== false) {
        setNotifications(prev => prev.map(notif => ({ ...notif, lue: true, read: true })));
        setUnreadCount(0);

      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.warn('Notification error:', error);
    }
  }, [token]);

  const showToast = useCallback((toastData) => {
    setToast(toastData);
    setTimeout(() => setToast(null), 5000);
  }, []);

  // ✅ INITIALISATION COMBINÉE
  useEffect(() => {


    return () => {

      disconnectSSE();
      disconnectMessageSocket();
    };
  }, [disconnectSSE, disconnectMessageSocket]);

  useEffect(() => {
    if (!user || !token) return;
    if (hasInitializedRef.current) {

      return;
    }
    if (connectedUsers.has(user._id)) {

      return;
    }


    hasInitializedRef.current = true;
    connectedUsers.add(user._id);

    // Charger les notifications initiales (SSE)
    loadInitialNotifications();
    
    // Connecter SSE après un délai
    setTimeout(() => connectSSE(), 1000);
    
    // Connecter Socket.IO pour les messages
    setTimeout(() => initializeMessageSocket(), 1500);

    return () => {
      disconnectSSE();
      disconnectMessageSocket();
    };
  }, [user, token, connectSSE, disconnectSSE, loadInitialNotifications, initializeMessageSocket, disconnectMessageSocket]);

  // ✅ RETOUR UNIFIÉ
  return {
    // Notifications générales (existant)
    notifications,
    unreadCount,
    isConnected,
    toast,
    markAsRead,
    markAllAsRead,
    showToast,
    clearToast: () => setToast(null),
    refreshNotifications: loadInitialNotifications,
    isLoading,

    // Notifications messages (nouveau)
    unreadMessages,
    totalUnreadMessages,
    markMessagesAsRead,
    markAllMessagesAsRead,
    getUnreadMessageCount,
    incrementUnreadMessageCount,

    // Alias pour compatibilité
    totalUnread: totalUnreadMessages, // pour le badge dans le menu
    markAsRead: markMessagesAsRead, // alias pour les messages
    getUnreadCount: getUnreadMessageCount // alias pour les messages
  };
};